import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, HStack, Text } from '@chakra-ui/react';

import useCompanyFeatures from '../../../../../components/Authorizer/useCompanyFeatures';
import { CypressIds } from '../../../../../constants/cypressIds';
import { FeatureTypes } from '../../../../../constants/featureTypes';
import generalFileImage from '../../../../../general-files.png';
import { createErrorToast } from '../../../../../redux/modules/app';
import { selectCompanyId } from '../../../../../redux/modules/company';
import { hideModal, showModal } from '../../../../../redux/modules/modal';
import { globalRequirementTemplateBuilder } from '../../../../../redux/reducer';
import taskDoneImage from '../../../../../task-done.png';
import uploadFileImage from '../../../../../upload-file.png';
import ModalOptionBlock from '../ModalOptionBlock';
import TemplatesFromContractModal from '../TemplatesFromContractModal';
import TemplatesListModal from '../TemplatesListModal';

const CreateTemplateModal = () => {
  const dispatch = useDispatch();
  const currentCompanyId = useSelector(selectCompanyId);
  const history = useHistory();

  const { companyHasFeature: aiScanEnabled } = useCompanyFeatures(FeatureTypes.AiTierOne);

  const descriptionText = useMemo(() => (aiScanEnabled
    ? 'Create a completely new template, start with an existing template, or extract insurance requirements from a template and modify it as needed.'
    : 'Create a completely new template or start with an existing template and modify it as needed.'), [aiScanEnabled]);

  const showTemplatesListModal = () => {
    dispatch(showModal(
      () => <TemplatesListModal />,
      {
        size: '4xl',
        title: 'Select Template',
        withHistory: true,
        buttons: { withHistoryButton: true }
      }
    ));
  };

  const showCreateTemplateFromContractModal = () => {
    dispatch(showModal(
      () => <TemplatesFromContractModal companyId={currentCompanyId} />,
      {
        size: '4xl',
        title: 'Create From Contract',
        withHistory: true,
        actionRequired: true,
        buttons: { withHistoryButton: true }
      }
    ));
  };

  const createTemplateFromScratch = async () => {
    const response = await dispatch(globalRequirementTemplateBuilder.createRequirementTemplate({
      name: 'Untitled',
      publishedAt: null,
      description: ' '
    }));

    if (response?.id) {
      history.replace(`/companies/${currentCompanyId}/create-templates/requirement-templates/${response.id}`);
      dispatch(hideModal());
    } else {
      dispatch(createErrorToast({
        title: 'Uh oh, something went wrong.',
        description: 'Please try again or contact support'
      }));

      dispatch(hideModal());
    }
  };

  return (
    <Box height="420px" margin="auto">
      <Text pb="3rem" textStyle="bodyLg500">
        {descriptionText}
      </Text>

      <HStack justifyContent="center" spacing={8}>
        <ModalOptionBlock
          cypressId={CypressIds.reqTemplatesCreateNewTemplateFromScratchButton}
          description="Create a completely new template."
          imgSrc={generalFileImage}
          title="Start from Scratch"
          onClick={createTemplateFromScratch}
        />
        <ModalOptionBlock
          cypressId={CypressIds.reqTemplatesCreateNewTemplateFromTemplateButton}
          description="Create a new template starting from an existing template."
          imgSrc={taskDoneImage}
          title="Use Template"
          onClick={showTemplatesListModal}
        />
        {aiScanEnabled && (
          <ModalOptionBlock
            cypressId={CypressIds.reqTemplatesCreateNewTemplateFromContractButton}
            description="Extract insurance requirements from a contract."
            imgSrc={uploadFileImage}
            title="Create from Contract"
            onClick={showCreateTemplateFromContractModal}
          />
        )}
      </HStack>
    </Box>
  );
};

export default CreateTemplateModal;
